import { useCallback } from 'react';
import { type GetAssetsActionButtonsVisibility } from './types';
import { useDepositWithdrawAssetsDictionary } from '@/shared/api';
import type { AccountType } from '@/entities/account';
import { useAssetTransferAvailability } from '@/entities/account';

const useAssetsActionButtons = ({
  isVerified,
  accountsQty,
}: {
  isVerified: boolean;
  accountsQty: number;
}) => {
  const { depositWithdrawAssetsData } = useDepositWithdrawAssetsDictionary();
  const { getAccountTypeTransferAvailability } = useAssetTransferAvailability();

  const getButtonsVisibility: GetAssetsActionButtonsVisibility = useCallback(
    (accountType: AccountType, assetCode: string, assetBalance: number) => {
      const isDepositAvailable = depositWithdrawAssetsData
        ? depositWithdrawAssetsData.value.deposit.all.includes(assetCode)
        : true;
      const isWithdrawAvailable = depositWithdrawAssetsData
        ? depositWithdrawAssetsData.value.withdraw.all.includes(assetCode)
        : true;

      switch (accountType) {
        case 'ewallet':
          return {
            deposit: isVerified && isDepositAvailable,
            withdraw: isVerified && assetBalance > 0 && isWithdrawAvailable,
            exchange: isVerified && assetBalance > 0,
            transfer: getAccountTypeTransferAvailability(accountType),
            trade: false,
          };
        case 'spot':
        case 'leverage':
        case 'invest':
          return {
            deposit: false,
            withdraw: false,
            exchange: false,
            trade: true,
            transfer: getAccountTypeTransferAvailability(accountType),
          };
        default:
          return {
            deposit: false,
            withdraw: false,
            exchange: false,
            transfer: false,
            trade: false,
          };
      }
    },
    [
      depositWithdrawAssetsData,
      isVerified,
      accountsQty,
      getAccountTypeTransferAvailability,
    ]
  );

  return {
    getButtonsVisibility,
  };
};

export { useAssetsActionButtons };
